import React from 'react';
import { useAuthStore } from './authStore';
import { logger } from '../utils/logger';

interface AuthContextType {
  user: any;
  tokens: any;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (provider: 'google' | 'yandex' | 'vk', code: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuthStore();
  
  logger.info('[AuthProvider] Component mounted', {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    hasToken: !!auth.tokens?.access_token,
  });
  
  React.useEffect(() => {
    logger.info('[AuthProvider] useEffect triggered', {
      hasToken: !!auth.tokens?.access_token,
      isLoading: auth.isLoading,
    });
    
    const checkAuth = async () => {
      logger.info('[AuthProvider] Starting auth check...');
      
      try {
        if (auth.tokens?.access_token) {
          logger.info('[AuthProvider] Token found, validating...');
          await auth.checkAuth();
          logger.info('[AuthProvider] Auth check completed', {
            isAuthenticated: auth.isAuthenticated,
            user: auth.user?.email,
          });
        } else {
          logger.info('[AuthProvider] No token, setting as guest');
          await auth.checkAuth();
          logger.info('[AuthProvider] Guest mode activated');
        }
      } catch (error) {
        logger.error('[AuthProvider] Auth check failed', { error });
      }
    };
    
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
